import React, { useEffect, useState } from 'react';
import {
  IcLineChart,
  IcBanknote,
  IcWallet,
  IcShoppingCart,
  IcBoxes,
  IcPackageOpen,
  IcSparkles,
  IcTrophy,
  IcLayers,
} from '../../theme/icons';
import SalesTrendChart from '../../components/SalesTrendChart';
import statisticsService from '../../services/statisticsService';
import { fmt, PeriodFilterButton, GoldenBestDay, WhiteCard, CategoryBars } from '../components/statsCommon';
import { StatCard, Delta } from '../../pages/statistics/statsUi';

export default function MobileSalesStatistics() {
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    statisticsService.getStatistics(period)
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch(() => { if (alive) setError('Impossible de charger les statistiques.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  const s = data?.summary || {};
  const p = data?.prev || {};
  const encaissePct = s.ca > 0 ? Math.min(100, (s.encaisse / s.ca) * 100) : 0;

  return (
    <div className="w-full px-4 py-6">
      {/* Nom de page = chip blanc bordure noire, comme MobileProductList
          (Studio 2026-08-14) ; ancien h1 blanc retiré, label de période conservé. */}
      {/* Filtre de période sur sa PROPRE ligne sous le titre, aligné à
          droite (Ahmed 2026-08-17). */}
      {/* Nom de page = pill blanche, texte encre 700 (Brand Kit §06 mock). */}
      <div className="mb-3">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-white text-gray-900">
          <IcLineChart size={13} /> Statistique de ventes
        </span>
      </div>
      {/* « 30 derniers jours » retiré aussi ici (Studio 2026-08-17). */}
      <PeriodFilterButton period={period} setPeriod={setPeriod} align="right" />

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-xl">{error}</div>}

      {loading && !data ? (
        <div className="text-center py-20"><div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
      ) : (
        <div className="space-y-5">
          {/* Évolution — courbe sans fond blanc (Ahmed 2026-08-16). Titre posé
              sur le fond sombre (titre de graphe = #F0FDF4 green-50 comme la
              carte « Ventes / jour » du livre §06) : `mk-dark` = remap clair. */}
          <div className="mk-dark">
            <div className="flex items-center gap-2 mb-2 px-1">
              <IcLineChart className="text-green-50 shrink-0" />
              <h2 className="font-semibold text-green-50">Évolution</h2>
            </div>
            <SalesTrendChart series={data?.series} transparent />
          </div>

          {/* Best day */}
          <GoldenBestDay bestDay={data?.best_day} />

          {/* KPI — mêmes cartes que la page PC (StatCard desktop) : même
              ordre, même détail, mêmes couleurs (Ahmed 2026-08-17). */}
          {/* Chaque carte KPI seule sur sa ligne (Ahmed 2026-08-17). */}
          <div className="grid grid-cols-1 gap-3">
            <StatCard icon={IcShoppingCart} label="Ventes" tone="slate"
              value={`${s.count || 0}`} curr={s.count} prev={p.count}
              sub={`Panier moyen ${fmt(s.avg_ticket)}`} />
            <StatCard icon={IcBanknote} label="Chiffre d'affaires" tone="green"
              value={fmt(s.ca)} curr={s.ca} prev={p.ca} />
            <StatCard icon={IcWallet} label="Encaissé" tone="amber"
              value={fmt(s.encaisse)} curr={s.encaisse} prev={p.encaisse}
              sub={`Crédit donné ${fmt(s.credit)}`}>
              <div className="mt-2 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden"
                title={`${encaissePct.toFixed(0)}% du CA encaissé`}>
                <div className="h-full rounded-full bg-amber-500" style={{ width: `${encaissePct}%` }} />
              </div>
            </StatCard>
            <StatCard icon={IcPackageOpen} label="Coût des ventes" tone="red"
              value={fmt(s.capital)} curr={s.capital} prev={p.capital} invert
              sub="Capital marchandise vendu" />
            <StatCard icon={IcLineChart} label="Bénéfice brut" tone="blue"
              value={fmt(s.benefice)} curr={s.benefice} prev={p.benefice}
              sub={`Marge ${(s.marge || 0).toFixed(0)}% du CA`} />
            {/* Case divisée en 2 : Articles vendus | Services — identité
                Services = IcSparkles + vert spruce green-700 (comme PC). */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 min-w-0 grid grid-cols-2 divide-x divide-gray-100">
              <div className="pr-3 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-7 w-7 shrink-0 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                    <IcBoxes size={12} />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 truncate">Articles</span>
                </div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-xl font-bold tabular-nums truncate text-blue-700">{s.qty_sold || 0}</span>
                  <Delta curr={s.qty_sold} prev={p.qty_sold} />
                </div>
                <div className="text-[11px] text-gray-500 mt-1 truncate">Remises {fmt(s.discount)}</div>
              </div>
              <div className="pl-3 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-7 w-7 shrink-0 rounded-lg flex items-center justify-center bg-green-50 text-green-700">
                    <IcSparkles size={12} />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 truncate">Services</span>
                </div>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-xl font-bold tabular-nums truncate text-green-700">{s.soins || 0}</span>
                  <Delta curr={s.soins} prev={p.soins} />
                </div>
                <div className="text-[11px] text-gray-500 mt-1 truncate">{fmt(s.soins_total)}</div>
              </div>
            </div>
          </div>

          {/* Top products */}
          <WhiteCard title="Top produits" icon={IcTrophy} iconClass="text-amber-500">
            {(!data?.top_products || data.top_products.length === 0) ? (
              <p className="text-gray-400 text-sm text-center py-8">Aucune vente sur la période.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-100">
                      <th className="py-2 font-medium">Produit</th>
                      <th className="py-2 font-medium text-right">Qté</th>
                      <th className="py-2 font-medium text-right">CA</th>
                      <th className="py-2 font-medium text-right">Bénéfice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_products.map((t, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-2 pr-2 font-medium text-gray-800 truncate max-w-[150px]">{t.name}</td>
                        <td className="py-2 text-right text-gray-600">{t.qty}</td>
                        <td className="py-2 text-right font-semibold text-green-700">{fmt(t.revenue)}</td>
                        <td className="py-2 text-right font-semibold text-blue-700">{fmt(t.benefice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </WhiteCard>

          {/* By category */}
          <WhiteCard title="CA par catégorie" icon={IcLayers} iconClass="text-blue-600">
            <CategoryBars items={data?.by_category} valueKey="revenue" />
          </WhiteCard>
        </div>
      )}
    </div>
  );
}
